import { fetch } from './api.js';

export async function getPublishedPosts(page = 1, limit = 10, includeDrafts = false, category = '') {
  const skip = (page - 1) * limit;
  const params = `skip=${skip}&limit=${limit}${includeDrafts ? '&all=true' : ''}${category ? `&category=${encodeURIComponent(category)}` : ''}`;
  const data = await fetch(`/parse/classes/Post?${params}`);
  return {
    posts: data.results || [],
    total: data.count || 0,
    page,
    limit,
    totalPages: Math.ceil((data.count || 0) / limit),
  };
}

export async function getPostBySlug(slug, includeDrafts = false) {
  try {
    const allParam = includeDrafts ? '?all=true' : '';
    const data = await fetch(`/parse/classes/Post/slug/${slug}${allParam}`);
    return data;
  } catch (err) {
    if (err.message.includes('404')) return null;
    throw err;
  }
}

export async function getPostById(id) {
  return await fetch(`/parse/classes/Post/${id}`);
}

export async function createPost(postData) {
  return await fetch('/parse/classes/Post', {
    method: 'POST',
    body: JSON.stringify(postData),
  });
}

export async function updatePost(id, postData) {
  return await fetch(`/parse/classes/Post/${id}`, {
    method: 'PUT',
    body: JSON.stringify(postData),
  });
}

export async function incrementPostView(id) {
  return await fetch(`/parse/classes/Post/${id}/view`, { method: 'POST' });
}

export async function deletePost(id) {
  return await fetch(`/parse/classes/Post/${id}`, {
    method: 'DELETE',
  });
}

export async function getAllPosts({ includeDrafts = false, skip = 0, limit = 10 } = {}) {
  const params = new URLSearchParams();
  if (includeDrafts) params.set('all', 'true');
  params.set('skip', String(skip));
  params.set('limit', String(limit));
  const data = await fetch(`/parse/classes/Post?${params}`);
  return { results: data.results || [], count: data.count || 0 };
}

export async function getSitemapData() {
  try {
    const data = await fetch('/parse/functions/sitemap');
    return data.result || [];
  } catch {
    return [];
  }
}

// Pages services
export async function getAllPages(includeDrafts = false) {
  const query = includeDrafts ? '?all=true' : '';
  const data = await fetch(`/parse/classes/Page${query}`);
  return data.results || [];
}

export async function getPageBySlug(slug, includeDrafts = false) {
  try {
    const allParam = includeDrafts ? '?all=true' : '';
    const data = await fetch(`/parse/classes/Page/slug/${slug}${allParam}`);
    return data;
  } catch (err) {
    if (err.message.includes('404')) return null;
    throw err;
  }
}

export async function getPageById(id, includeDrafts = false) {
  try {
    const allParam = includeDrafts ? '&all=true' : '';
    const data = await fetch(`/parse/classes/Page/id/${id}?limit=1${allParam}`);
    return data;
  } catch (err) {
    if (err.message.includes('404')) return null;
    throw err;
  }
}

export async function createPage(pageData) {
  return await fetch('/parse/classes/Page', {
    method: 'POST',
    body: JSON.stringify(pageData),
  });
}

export async function updatePage(id, pageData) {
  return await fetch(`/parse/classes/Page/${id}`, {
    method: 'PUT',
    body: JSON.stringify(pageData),
  });
}

export async function deletePage(id) {
  return await fetch(`/parse/classes/Page/${id}`, {
    method: 'DELETE',
  });
}

// Menus services
export async function getMenuItems(menu = 'principal', options = {}) {
  const data = await fetch(`/parse/classes/MenuItem?menu=${menu}`, options);
  return data.results || [];
}

export async function createMenuItem(itemData) {
  return await fetch('/parse/classes/MenuItem', {
    method: 'POST',
    body: JSON.stringify(itemData),
  });
}

export async function updateMenuItem(id, itemData) {
  return await fetch(`/parse/classes/MenuItem/${id}`, {
    method: 'PUT',
    body: JSON.stringify(itemData),
  });
}

export async function deleteMenuItem(id) {
  return await fetch(`/parse/classes/MenuItem/${id}`, {
    method: 'DELETE',
  });
}

export async function reorderMenuItems(items) {
  return await fetch('/parse/classes/MenuItem/reorder', {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
}

// Slider services
export async function getSliderItems() {
  const data = await fetch('/parse/classes/SliderItem');
  return data.results || [];
}

export async function createSliderItem(itemData) {
  return await fetch('/parse/classes/SliderItem', {
    method: 'POST',
    body: JSON.stringify(itemData),
  });
}

export async function updateSliderItem(id, itemData) {
  return await fetch(`/parse/classes/SliderItem/${id}`, {
    method: 'PUT',
    body: JSON.stringify(itemData),
  });
}

export async function deleteSliderItem(id) {
  return await fetch(`/parse/classes/SliderItem/${id}`, {
    method: 'DELETE',
  });
}

export async function reorderSliderItems(items) {
  return await fetch('/parse/classes/SliderItem/reorder', {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
}

// Categories services
export async function getAllCategories() {
  const data = await fetch('/parse/classes/Category');
  return data.results || [];
}

export async function getCategoryById(id) {
  try {
    return await fetch(`/parse/classes/Category/${id}`);
  } catch (err) {
    if (err.message.includes('404')) return null;
    throw err;
  }
}

export async function createCategory(categoryData) {
  return await fetch('/parse/classes/Category', {
    method: 'POST',
    body: JSON.stringify(categoryData),
  });
}

export async function updateCategory(id, categoryData) {
  return await fetch(`/parse/classes/Category/${id}`, {
    method: 'PUT',
    body: JSON.stringify(categoryData),
  });
}

export async function deleteCategory(id) {
  return await fetch(`/parse/classes/Category/${id}`, {
    method: 'DELETE',
  });
}

// Product services
export async function getAllProducts({ includeDrafts = false, skip, limit, category } = {}) {
  const params = new URLSearchParams();
  if (includeDrafts) params.set('all', 'true');
  if (skip != null) params.set('skip', String(skip));
  if (limit != null) params.set('limit', String(limit));
  if (category) params.set('category', category);
  const qs = params.toString();
  const data = await fetch(`/parse/classes/Product${qs ? `?${qs}` : ''}`);
  return { results: data.results || [], count: data.count || 0 };
}

export async function getProductBySlug(slug, includeDrafts = false) {
  try {
    const allParam = includeDrafts ? '?all=true' : '';
    const data = await fetch(`/parse/classes/Product/slug/${slug}${allParam}`);
    const results = data.results || [];
    return results[0] || null;
  } catch (err) {
    if (err.message.includes('404')) return null;
    throw err;
  }
}

export async function getProductById(id) {
  try {
    return await fetch(`/parse/classes/Product/${id}`);
  } catch (err) {
    if (err.message.includes('404')) return null;
    throw err;
  }
}

export async function createProduct(productData) {
  return await fetch('/parse/classes/Product', {
    method: 'POST',
    body: JSON.stringify(productData),
  });
}

export async function updateProduct(id, productData) {
  return await fetch(`/parse/classes/Product/${id}`, {
    method: 'PUT',
    body: JSON.stringify(productData),
  });
}

export async function deleteProduct(id) {
  return await fetch(`/parse/classes/Product/${id}`, {
    method: 'DELETE',
  });
}

// Product Categories services
export async function getAllProductCategories() {
  const data = await fetch('/parse/classes/ProductCategory');
  return data.results || [];
}

export async function getProductCategoryById(id) {
  try {
    return await fetch(`/parse/classes/ProductCategory/${id}`);
  } catch (err) {
    if (err.message.includes('404')) return null;
    throw err;
  }
}

export async function createProductCategory(categoryData) {
  return await fetch('/parse/classes/ProductCategory', {
    method: 'POST',
    body: JSON.stringify(categoryData),
  });
}

export async function updateProductCategory(id, categoryData) {
  return await fetch(`/parse/classes/ProductCategory/${id}`, {
    method: 'PUT',
    body: JSON.stringify(categoryData),
  });
}

export async function deleteProductCategory(id) {
  return await fetch(`/parse/classes/ProductCategory/${id}`, {
    method: 'DELETE',
  });
}
